// History of Rx that have been filed into a processing software (PK / PioneerRx /
// ReComRx) by the bot (or manually via clipboard / file import). Newest first.
// Server-side patient-name search.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../lib/api";
import type { ImportedRow, TargetPms } from "@shared/types";
import { TARGET_PMS_LABEL } from "@shared/types";

const STORAGE_STORE_KEY = "smartrx-entry.currentStoreCode";

function getCurrentStoreCode(): string {
  return (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_STORE_KEY)) || "GV";
}

const METHOD_LABELS: Record<ImportedRow["method"], string> = {
  file_import: "File import",
  pad_bridge:  "PAD bot",
  clipboard:   "Clipboard",
};

export function Imported() {
  const [rows, setRows] = useState<ImportedRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sinceDays, setSinceDays] = useState<string>("30");  // '7' | '30' | '90' | 'all'
  const storeCode = getCurrentStoreCode();
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const ctrl = new AbortController();
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      const qs = `store=${storeCode}&sinceDays=${sinceDays}${query.trim() ? `&q=${encodeURIComponent(query.trim())}` : ""}`;
      apiGet<ImportedRow[]>(`/imported?${qs}`, ctrl.signal)
        .then((data) => { setRows(data); setError(null); })
        .catch((e) => { if (!ctrl.signal.aborted) { setRows([]); setError(String(e)); } })
        .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    }, 200);
    return () => { ctrl.abort(); if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [query, storeCode, sinceDays]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-2xl font-semibold">Imported to Processing Software</h2>
        <span className="text-ink-soft text-sm">{storeCode}</span>
      </div>
      <p className="text-ink-soft mb-6">
        {error
          ? <span className="text-bad">History unavailable: {error}</span>
          : `${rows.length} record${rows.length === 1 ? "" : "s"} filed${query.trim() ? ` matching "${query.trim()}"` : ""}.`}
      </p>

      <div className="mb-4 flex gap-3 items-center">
        <input
          type="search"
          placeholder="Search by patient name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full max-w-sm px-3 py-2 rounded border border-border bg-surface text-sm focus:outline-none focus:border-accent"
        />
        <label className="text-ink-soft text-sm flex items-center gap-2">
          Show:
          <select value={sinceDays} onChange={(e) => setSinceDays(e.target.value)}
            className="border border-border rounded px-2 py-1 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-accent">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </label>
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[64px_1.1fr_1.1fr_140px_100px_120px_1fr_88px] gap-3 px-4 py-2 text-xs uppercase tracking-wide text-ink-soft border-b border-border bg-pill">
          <span>#</span>
          <span>Patient</span>
          <span>Drug</span>
          <span>Filed</span>
          <span>PMS</span>
          <span>Method</span>
          <span>Reviewed by</span>
          <span>PMS Rx #</span>
        </div>
        {loading && rows.length === 0 && (
          <p className="text-ink-soft text-sm py-6 text-center">Loading…</p>
        )}
        {!loading && rows.length === 0 && !error && (
          <p className="text-ink-soft text-sm py-10 text-center">
            No imported prescriptions for {storeCode}{query.trim() ? " match that search" : " yet"}.
          </p>
        )}
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <Link
              key={r.rxId}
              to={`/review/${r.rxId}`}
              className="grid grid-cols-[64px_1.1fr_1.1fr_140px_100px_120px_1fr_88px] gap-3 px-4 py-2.5 items-center text-sm hover:bg-pill"
            >
              <span className="text-ink-soft">#{r.rxId}</span>
              <span className="truncate"><strong>{r.patientName ?? "—"}</strong></span>
              <span className="truncate">{r.drug}</span>
              <span className="text-ink-soft" title={r.filedAt}>{formatDateTime(r.filedAt)}</span>
              <span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-accent-soft text-accent">
                  {r.targetPms ? TARGET_PMS_LABEL[r.targetPms as TargetPms] ?? r.targetPms : "—"}
                </span>
              </span>
              <span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-pill text-ink-soft">{METHOD_LABELS[r.method] ?? r.method}</span>
              </span>
              <span className="truncate text-ink-soft">{r.reviewedByName ?? "—"}</span>
              <span className="truncate font-mono text-xs">{r.pkRxNumber ?? "—"}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
